/**
 * Form state + validação + build dos requests de participante do Bond.
 *
 * Add: (personId XOR userId) + role (obrigatório) + permissions[] + permissionScope
 *   + side? + isPrimary? + reason? — espelha AddParticipantDto.
 * Update: role? + permissions[] + permissionScope? + reason? — espelha UpdateParticipantDto.
 * Revoke: reason? — vai via QUERY param (não body), com If-Match version.
 *
 * subjectKind alterna qual identificador é coletado (Pessoa externa ↔ Usuário
 * interno): o backend aceita um OU outro (XOR). personId/userId são honest-defer
 * de pickers (texto), igual ao personId do B1/B2.
 */

import type { AddParticipantRequest, UpdateParticipantRequest } from '../../../lib/api-client';

export type SubjectKind = 'person' | 'user';

export interface ParticipantFormState {
  subjectKind: SubjectKind;
  personId: string;
  userId: string;
  role: string;
  permissions: string[];
  permissionScope: string;
  side: string;
  isPrimary: boolean;
}

export const DEFAULT_SCOPE = 'full';

export function initialAddForm(): ParticipantFormState {
  return {
    subjectKind: 'person',
    personId: '',
    userId: '',
    role: '',
    permissions: [],
    permissionScope: DEFAULT_SCOPE,
    side: '',
    isPrimary: false,
  };
}

/** Form de edição pré-preenchido a partir do participante existente. */
export function editFormFrom(p: {
  personId: string | null;
  userId: string | null;
  role: string;
  permissions: string[];
  permissionScope: string;
  side: string | null;
  reason: string | null;
}): ParticipantFormState {
  return {
    subjectKind: p.userId !== null ? 'user' : 'person',
    personId: p.personId ?? '',
    userId: p.userId ?? '',
    role: p.role,
    permissions: [...p.permissions],
    permissionScope: p.permissionScope,
    side: p.side ?? '',
    isPrimary: false,
  };
}

export function togglePermission(permissions: string[], key: string): string[] {
  return permissions.includes(key) ? permissions.filter((p) => p !== key) : [...permissions, key];
}

/** Validação do add: exatamente um identificador (conforme subjectKind) + role. */
export function validateAddForm(form: ParticipantFormState): string | null {
  if (form.subjectKind === 'person' && form.personId.trim() === '') {
    return 'Informe o ID da pessoa.';
  }
  if (form.subjectKind === 'user' && form.userId.trim() === '') {
    return 'Informe o ID do usuário interno.';
  }
  if (form.role.trim() === '') return 'Informe o papel do participante.';
  return null;
}

/** Validação do edit (role obrigatório; identificador é imutável aqui). */
export function validateEditForm(form: ParticipantFormState): string | null {
  if (form.role.trim() === '') return 'Informe o papel do participante.';
  return null;
}

export function buildAddRequest(form: ParticipantFormState): AddParticipantRequest {
  const req: AddParticipantRequest = { role: form.role.trim() };
  if (form.subjectKind === 'person') req.personId = form.personId.trim();
  else req.userId = form.userId.trim();
  if (form.permissions.length > 0) req.permissions = [...form.permissions];
  if (form.permissionScope.trim() !== '') req.permissionScope = form.permissionScope;
  if (form.side.trim() !== '') req.side = form.side;
  if (form.isPrimary) req.isPrimary = true;
  return req;
}

export function buildUpdateRequest(form: ParticipantFormState): UpdateParticipantRequest {
  return {
    role: form.role.trim(),
    permissions: [...form.permissions],
    permissionScope: form.permissionScope,
  };
}
